import { useState, useCallback } from "react";
import type { InviteEntryData } from "./InviteEntry";
import {
  validateAllEntries,
  validateInviteEntry,
  type ValidationOptions,
} from "./InviteValidation";

const emptyEntry: InviteEntryData = {
  firstName: "",
  lastName: "",
  email: "",
  customerId: "",
  phone: "",
  customerHistoryFile: null,
};

export function useInviteFormState(
  maxEntries: number = 20,
  validationOptions?: ValidationOptions,
) {
  const [entries, setEntries] = useState<InviteEntryData[]>([
    { ...emptyEntry },
  ]);
  const [entryErrors, setEntryErrors] = useState<Record<number, string | null>>(
    {},
  );

  const addEntry = useCallback(() => {
    if (entries.length < maxEntries) {
      setEntries([...entries, { ...emptyEntry }]);
    }
  }, [entries, maxEntries]);

  const removeEntry = useCallback(
    (index: number) => {
      if (entries.length > 1) {
        setEntries(entries.filter((_, i) => i !== index));
        // Clear error for removed entry
        setEntryErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }
    },
    [entries],
  );

  const updateEntry = useCallback((index: number, data: InviteEntryData) => {
    setEntries((prev) => {
      const newEntries = [...prev];
      newEntries[index] = data;
      return newEntries;
    });
    // Clear error for this entry when it changes
    setEntryErrors((prev) => {
      if (prev[index]) {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const validateAll = useCallback(() => {
    return validateAllEntries(entries, validationOptions);
  }, [entries, validationOptions]);

  const hasValidEntries = entries.some((entry) => {
    return validateInviteEntry(entry, validationOptions) === null;
  });

  const clearErrors = useCallback(() => {
    setEntryErrors({});
  }, []);

  const setErrors = useCallback((errors: Record<number, string>) => {
    setEntryErrors(errors);
  }, []);

  return {
    entries,
    entryErrors,
    addEntry,
    removeEntry,
    updateEntry,
    validateAll,
    hasValidEntries,
    clearErrors,
    setErrors,
  };
}
